import { useEffect, useRef } from 'react';
import type { PosHotkeyAccionId, PosHotkeyBindings } from '../config/posFacturaHotkeys';
import {
  invertPosHotkeyBindings,
  resolvePosHotkeyFromEvent,
} from '../config/posFacturaHotkeys';

export type PosHotkeyHandlerMap = Partial<
  Record<PosHotkeyAccionId, { handler: () => void; enabled?: boolean }>
>;

function isMuiDialogOpen(): boolean {
  return Array.from(document.querySelectorAll('.MuiDialog-root')).some((node) => {
    if (!(node instanceof HTMLElement)) return false;
    if (node.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return node.getClientRects().length > 0;
  });
}

interface UsePosConfigurableHotkeysOptions {
  bindings: PosHotkeyBindings;
  handlers: PosHotkeyHandlerMap;
  disabled?: boolean;
  /** Acciones que sí disparan con un Dialog abierto. */
  allowWhenDialogOpen?: PosHotkeyAccionId[];
}

/**
 * Atajos POS según bindings por usuario (acción → tecla).
 */
export function usePosConfigurableHotkeys({
  bindings,
  handlers,
  disabled = false,
  allowWhenDialogOpen = [],
}: UsePosConfigurableHotkeysOptions): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;
  const allowRef = useRef(allowWhenDialogOpen);
  allowRef.current = allowWhenDialogOpen;

  useEffect(() => {
    if (disabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const tecla = resolvePosHotkeyFromEvent(event);
      if (!tecla) return;

      const byKey = invertPosHotkeyBindings(bindingsRef.current);
      const accionId = byKey[tecla];
      if (!accionId) return;

      const action = handlersRef.current[accionId];
      if (!action || action.enabled === false) return;

      if (isMuiDialogOpen() && !allowRef.current.includes(accionId)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      action.handler();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [disabled]);
}
